import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { ASSIGNMENT_MODE_INSURANCE } from "components/Assignment";
import { getInspectorDetails, getAssignedCases } from "actions/assignment";
import { getStateList } from "actions/appraiser";
import { getUser, clearInternetUser } from "actions/user";
import { updateFieldRep, getAllTeamLeaders } from "actions/homeowner/fieldReps";
import { updateHeader, clearHeader } from "actions/layout";
import { getNewSearchResults } from "actions/search";
import responseHandler from "responseHandler";
import { 
    HR_INFO_EDIT,
    FIELD_REP_PROFILE_DESIGNATIONS_EDIT, 
    FIELD_REP_PROFILE_DESIGNATIONS_VIEW, 
    EMPLOYEENOTES_VIEW, 
    MANAGEMENT_CALENDAR_EDIT, 
    ALLOWEDITDATEFORFRTIMELOG,
    FIELD_REP_CAN_BE_ASSIGNED_CASES_EDIT,
    FIELD_REP_DISABLE_AUTO_ASSIGN_EDIT,
    EMPLOYEE_COURSE_VIEW,
    EMPLOYEE_COMPLAINT_VIEW,
} from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { stateListAbbreviationSelection } from "selectors/menuItems";
import { Grid } from "react-md";
import { FieldRepMacCard, UserInformation, LoginActivity } from "components/User";
import { FILTER_TYPE_NONE, FORMATTER_RIGHT_ALIGN } from "components/Shared/MGrid.tsx";
import { dateTimeToStringWithNumOfDays } from "components/Shared/Formatters";
import { GridCard, MCard, MSpinner } from "components/Shared";
import {
  getFRAddressInformationCard,
  getFRContactInformationCard,
  getFRRemarksCard,
  getFRHRInformationCard,
  getFROtherInformationCard,
  getFRDetailsCard,
  getFRDesignationsCard,
} from "typeConstants/mCardDefinitions/fieldRepProfile";
import CaseIssues from "components/Homeowner/FieldReps/CaseIssues";
import * as employeeRoles from "typeConstants/employeeRoles";
import { getEmployeesByRoles } from "requests/employee";
import ExclusionaryList from "components/Shared/ExclusionaryList";
import EmployeeCalendar from "components/Employee/EmployeeCalendar.tsx";
import AdminTimeLog from "components/Shared/AdminTimeLog";
import EmployeeTEAM from "components/Employee/EmployeeTEAM";
import FieldRepServiceRegion from "components/Homeowner/FieldReps/FieldRepServiceRegion";
import InspectorRegionManager from "components/InspectorRegionManager/index.tsx";
import EmployeeCourses from "components/Employee/EmployeeCourses";
import EmployeeComplaintCard from "components/Employee/EmployeeComplaintCard.tsx";

const FieldRepProfileV2 = props => {
  const [fieldLevelCoordinators, setFieldLevelCoordinators] = useState([]);
  const [fieldRepDevelopmentManagers, setFieldRepDevelopmentManagers] = useState([]);
  const [isEditingRegions, setIsEditingRegions] = useState(false);
  const mounted = useRef(true);
  const fieldRepId = useRef(null);

  useEffect(() => {
    if (!props.stateList.length) props.getStateList();
    if (!props.allTeamLeaders.length) props.getAllTeamLeaders();
    if (!fieldLevelCoordinators.length) {
      getEmployeesByRoles([employeeRoles.FIELD_OPERATIONS_MANAGER], data => setFieldLevelCoordinators(data), mounted);
    } 
    if (!fieldRepDevelopmentManagers.length) {
      getEmployeesByRoles([employeeRoles.FIELD_DEVELOPMENT_MANAGER], data => setFieldRepDevelopmentManagers(data), mounted);
    }
    props.updateHeader(getHeaderDefinitions());

    return () => {
      mounted.current = false;
      props.clearInternetUser();
      props.clearHeader();
    };
  }, []);

  useEffect(() => {
    let frNumber = getFRNumber();
    if (frNumber) {
      props.getInspectorDetails(frNumber);
      props.getAssignedCases(frNumber);
    }
  }, [props.frNumberOverride, props.location?.state?.frNumber]);

  useEffect(() => {
    let internetUserId = getInternetUserId();
    if (internetUserId) {
      props.getUser(props.internetUserIdOverride);
    }
  }, [props.internetUserIdOverride, props.location?.state?.internetUserId]);

  useEffect(() => {
    if (props.fieldRep?.inspectorLocation?.id) {
      fieldRepId.current = props.fieldRep?.inspectorLocation?.id;
    }
  }, [props.fieldRep]);

  const getFRNumber = () => {
    return props.frNumberOverride ?? props.location?.state?.frNumber;
  };

  const getInternetUserId = () => {
    return props.internetUserIdOverride ?? props.location?.state?.internetUserId;
  };

  const mdcellCard = (i, extraClass) => {
    return `card-unnested md-cell md-cell--${getCellWidth(i)} ${extraClass}`;
  };

  const getCellWidth = (w) => {
    return props.verticalDisplay ? 12 : (w || 4);
  };

  const saveFieldRepChanges = (keyValues) => {
    let frNumber = props.frNumberOverride || props.location.state.frNumber;
    // must reverse value for disableAutoAssign field before saving since display is its inverse
    let autoAssignIndex = keyValues.findIndex(obj => obj.key === "disableAutoAssign");
    if (autoAssignIndex  !== -1) {
      let originalVal = keyValues[autoAssignIndex].originalValue;
      keyValues[autoAssignIndex].originalValue = keyValues[autoAssignIndex].value;
      keyValues[autoAssignIndex].value = originalVal;
    }
    props.updateFieldRep(frNumber, keyValues, () => props.getInspectorDetails(frNumber));
  };

  const viewOrdersRedirect = () => {
    if (fieldRepId.current) {
      props.history.push({
        pathname: "/inspector-cases",
        state: {
          // Can't reference props.fieldRep?.inspectorLocation?.id directly here 
          // otherwise the value will get captured and not updated when FR selection changes
          inspectorId: fieldRepId.current,
        }
      });
    }
    
  };

  const getHeaderDefinitions = () => {
    return {
      dropdownTitle: "Field Rep Profile",
      buttons: [
        {
          name: "View Order Queue",
          onClick: viewOrdersRedirect
        }
      ]
    };
  };

  let isLoading = (props.isFetching ||
    !props.fieldRep ||
    props.isFetchingInternetUser ||
    Object.keys(props.internetUser).length === 0 ||
    props.isFetchingAssignedCases ||
    !props.assignedCases ||
    !props.stateList ||
    props.isFetchingAllTeamLeaders);

  if (isLoading) return (<MSpinner />);

  // Abbreviated profile
  let contactInfo = getFRContactInformationCard(props.fieldRep);
  let addressInfo = getFRAddressInformationCard(props.fieldRep, props.stateList);

  const getFrContactCard = () => {
    return (
      <MCard
          title={contactInfo.title}
          cellWidth={getCellWidth(4)}
          isEditable
          onEditFinished={saveFieldRepChanges}
          fields={contactInfo.fields}
        />);
  };

  const getFRAddressCard = () => {
    return (
        <MCard
          title={addressInfo.title}
          cellWidth={getCellWidth(4)}
          isEditable
          onEditFinished={saveFieldRepChanges}
          fields={addressInfo.fields}
        />      
    );
  };

  const getFrCalendarCard = () => {
    if(props.fieldRep.employeeId){
      return (
          <MCard
            title="Employee Calendar"
            cellWidth={getCellWidth(6)}
            fields={[
              {
                key: "calendar", 
                control: (
                  <EmployeeCalendar 
                    employeeId={props.fieldRep.employeeId} 
                    readOnly={props.canEditCalendarAsManager ? false : true}
                    allowManagementEdit={props.canEditCalendarAsManager}
                  />)
              }]}
          />     
      );
    } else return null;
  };

  const getFrQueueCard = () => {
    return (
      <div className={mdcellCard(12, "field-rep-queue md-grid-cards card-style")}>
        <GridCard
          gridCols={colDefs}
          headerText="Field Rep Queue"
          data={
            props.assignedCases.map(x => ({
              inspectionType: x.inspectionType,
              customerName: x.customerName,
              fullName: x.insuredFirstName + " " + x.insuredLastName,
              customerDueDate: x.caseLocation.customerDueDate ? dateTimeToStringWithNumOfDays(x.caseLocation.customerDueDate) : "n/a",
              dateAssigned: x.dateAssigned ? dateTimeToStringWithNumOfDays(x.dateAssigned) : "n/a",
              controlNumber: x.caseLocation.caseId,
              mac: "$" + x.mac,
              percentageEnhanced: x.caseLocation.frIncentivePercent,
              fieldAppointmentDate: x.fieldAppointmentDate ? dateTimeToStringWithNumOfDays(x.fieldAppointmentDate) : "n/a",
            }))
          } />
      </div>);
  };

  const getEmployeeCoursesCard = () => {
    if (props.canViewEmployeeCourses) {
      return <EmployeeCourses employeeId={props.fieldRep.employeeId} className={mdcellCard(12)} />;
    }
  };

  const getFrTeamCard = () => {
    if (props.canViewNotes) {
      return (
        <EmployeeTEAM 
        internetUserId={getInternetUserId()}
        cellWidth={getCellWidth(6)}
      />);
    }
  };

  const getFrLoginActivityCard = () => {
    return (  
      <LoginActivity
        className={mdcellCard(4)}
        loginStatistics={props.internetUser.loginStatistics} 
      />);
  };

  const getFrMacCard = () => {
    return (
      <div className={mdcellCard(6, "mac-list-cell md-grid-cards")}>
        <FieldRepMacCard
          inspectorNumber={props.fieldRep.inspectorLocation.inspectorNumber}
        />
      </div>);
  };

  const getAdminTimeLog = () => {
    return (
      <div className={mdcellCard(6, "md-grid-cards")}>
        <AdminTimeLog
          inspectorNumber={props.fieldRep.inspectorLocation.inspectorNumber}
          canAddAndEdit={props.canEditAdminTimeLog}
        />
      </div>);
  };

  const getUserInformationCard = () => {
    return (
      <div className={mdcellCard(4)}>
        <UserInformation
          userInformation={props.internetUser}
          internetUserId={props.internetUser.id}
          canEditUser={props.internetUser.canEditUser}
        />
      </div>
    );
  };

  const getOtherInfoCard = () => {
    const otherInfo = getFROtherInformationCard(props.fieldRep, props.allTeamLeaders, fieldLevelCoordinators, fieldRepDevelopmentManagers, props.canEditCanBeAssignedCases, props.canDisableAutoAssign);
    return (
      <MCard
        title={otherInfo.title}
        cellWidth={getCellWidth(4)}
        fields={otherInfo.fields}
        isEditable
        onEditFinished={saveFieldRepChanges}
      />
    );
  };

  const onRegionManagerClosed = () => {
    setIsEditingRegions(false);
    props.regionManagerClosedCallback?.();
  };
  
  const getFrServiceRegionCard = () => {
    return (
      <FieldRepServiceRegion
        cellWidth={getCellWidth(6)}
        onEditClicked={() => setIsEditingRegions(true)}
        isEditing={isEditingRegions}
      />
    );
  };

  let gridClassname = "field-rep-profile";

  if (isEditingRegions) gridClassname += " hidden";

  if (props.showAbbreviatedProfile) {
    return (
      <>
        {isEditingRegions && <InspectorRegionManager inspectorNumber={props.fieldRep.inspectorLocation.inspectorNumber} onClose={onRegionManagerClosed} />}
        <Grid className={gridClassname}>
          {getFRAddressCard()}
          {getFrContactCard()}
          {getFrQueueCard()}
          {getEmployeeCoursesCard()}
          {getFrTeamCard()}
          {getFrServiceRegionCard()}
          {getFrLoginActivityCard()}
          {getFrMacCard()}
          {getAdminTimeLog()}
          {getUserInformationCard()}
          {getOtherInfoCard()}
          {getFrCalendarCard()}
        </Grid>
      </>
    );
  }

  // Full profile
  let remarks = getFRRemarksCard(props.fieldRep);
  let hrInfo = getFRHRInformationCard(props.fieldRep);
  let details = getFRDetailsCard(props.fieldRep);
  let designations = getFRDesignationsCard({ fieldRep: props.fieldRep, onEditFinished: saveFieldRepChanges, userRights: props.rights });

  return (
    <>
      {isEditingRegions && <InspectorRegionManager inspectorNumber={props.fieldRep.inspectorLocation.inspectorNumber} onClose={onRegionManagerClosed} />}
      <Grid className={gridClassname}>
        {getFRAddressCard()}
        {getFrContactCard()}
        <MCard
          title={remarks.title}
          cellWidth={getCellWidth(4)}
          isEditable
          onEditFinished={saveFieldRepChanges}
          fields={remarks.fields}
        />
        {getFrCalendarCard()}
        {getFrTeamCard()}
        {props.canViewEmployeeComplaints ? <EmployeeComplaintCard employeeId={props.fieldRep.employeeId} cellWidth={getCellWidth(6)}/> : <></>}
        {getFrServiceRegionCard()}
        <MCard
          title={hrInfo.title}
          cellWidth={getCellWidth(4)}
          fields={hrInfo.fields}
          isEditable={props.canEditHrInfo}
          onEditFinished={saveFieldRepChanges}
        />
        {getOtherInfoCard()}
        <MCard
          title={details.title}
          className={mdcellCard(4)}
          fields={details.fields}
        />
        <MCard
          title="Exclusionary List"
          className={mdcellCard(4)}
          fields={[
            {
              key: "exclusions", control: (<ExclusionaryList inspectorNumber={props.fieldRep.inspectorLocation.inspectorNumber} />)
            }]}
        />
        {(props.canViewFrDesignations || props.canEditFrDesignations) && <MCard
          {...designations}
          className={mdcellCard(4)}
          isEditable={props.canEditFrDesignations}
        />}
        {getUserInformationCard()}
        {getFrLoginActivityCard()}
        {getFrMacCard()}
        {getAdminTimeLog()}
        <CaseIssues
          frNumber={getFRNumber()}
          className={mdcellCard(12)} />
        {getFrQueueCard()}
        {getEmployeeCoursesCard()}
      </Grid>
    </>
  );
};

FieldRepProfileV2.propTypes = {
  frNumberOverride: PropTypes.number,
  internetUserIdOverride: PropTypes.number,
  getInspectorDetails: PropTypes.func.isRequired,
  updateFieldRep: PropTypes.func.isRequired,
  getStateList: PropTypes.func.isRequired,
  stateList: PropTypes.array,
  isFetching: PropTypes.bool.isRequired,
  fieldRep: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object,
  getUser: PropTypes.func.isRequired,
  isFetchingInternetUser: PropTypes.bool.isRequired,
  internetUser: PropTypes.object.isRequired,
  verticalDisplay: PropTypes.bool,
  clearInternetUser: PropTypes.func.isRequired,
  getAssignedCases: PropTypes.func.isRequired,
  isFetchingAssignedCases: PropTypes.bool.isRequired,
  assignedCases: PropTypes.array,
  allTeamLeaders: PropTypes.array.isRequired,
  getAllTeamLeaders: PropTypes.func.isRequired,
  isFetchingAllTeamLeaders: PropTypes.bool.isRequired,
  getNewSearchResults: PropTypes.func,
  updateHeader: PropTypes.func,
  clearHeader: PropTypes.func,
  canEditHrInfo: PropTypes.bool,  // Allows HR Info card to be editable
  makeToast: PropTypes.func,
  canEditFrDesignations: PropTypes.bool,
  canViewFrDesignations: PropTypes.bool,
  canViewNotes: PropTypes.bool,
  canEditCalendarAsManager: PropTypes.bool,
  canEditAdminTimeLog: PropTypes.bool,
  canEditCanBeAssignedCases: PropTypes.bool,
  canDisableAutoAssign: PropTypes.bool,
  canViewEmployeeCourses: PropTypes.bool,
  showAbbreviatedProfile: PropTypes.bool, // Show limited selection of cards
  rights: PropTypes.arrayOf(PropTypes.string),
  regionManagerClosedCallback: PropTypes.func,
  canViewEmployeeComplaints: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    isFetching: state.assignment.isFetchingInspectorDetails,
    fieldRep: state.assignment.inspectorDetails,
    isFetchingInternetUser: state.user.isFetchingInternetUser,
    internetUser: state.user.internetUser,
    isFetchingAssignedCases: state.assignment.isFetchingAssignedCases,
    assignedCases: state.assignment.assignedCases,
    stateList: stateListAbbreviationSelection(state),
    isFetchingAllTeamLeaders: state.fieldRep.isFetchingAllTeamLeaders,
    allTeamLeaders: state.fieldRep.allTeamLeaders,
    canEditHrInfo: userHasRight(HR_INFO_EDIT)(state),
    canEditFrDesignations: userHasRight(FIELD_REP_PROFILE_DESIGNATIONS_EDIT)(state),
    canViewFrDesignations: userHasRight(FIELD_REP_PROFILE_DESIGNATIONS_VIEW)(state),
    canViewNotes: userHasRight(EMPLOYEENOTES_VIEW)(state),
    canEditCalendarAsManager: userHasRight(MANAGEMENT_CALENDAR_EDIT)(state),
    canEditAdminTimeLog: userHasRight(ALLOWEDITDATEFORFRTIMELOG)(state),
    canEditCanBeAssignedCases: userHasRight(FIELD_REP_CAN_BE_ASSIGNED_CASES_EDIT)(state),
    canDisableAutoAssign: userHasRight(FIELD_REP_DISABLE_AUTO_ASSIGN_EDIT)(state),
    canViewEmployeeCourses: userHasRight(EMPLOYEE_COURSE_VIEW)(state),
    canViewEmployeeComplaints: userHasRight(EMPLOYEE_COMPLAINT_VIEW)(state),
    rights: state.user.profile.rights,
});

const mapDispatchToProps = (dispatch) => ({
    getInspectorDetails(frNumber) {
        dispatch(getInspectorDetails(ASSIGNMENT_MODE_INSURANCE, frNumber));
    },
    getUser(id) {
        dispatch(getUser(id));
    },
    clearInternetUser() {
        dispatch(clearInternetUser());
    },
    getAssignedCases(frNumber) {
        dispatch(getAssignedCases(ASSIGNMENT_MODE_INSURANCE, frNumber));
    },
    getStateList() {
        dispatch(getStateList());
    },
    updateFieldRep(frNumber, keyValues, callback) {
        dispatch(updateFieldRep(frNumber, keyValues, callback));
    },
    getAllTeamLeaders() {
        dispatch(getAllTeamLeaders());
    },
    updateHeader(headerDefinitions) {
        dispatch(updateHeader(headerDefinitions));
    },
    clearHeader() {
        dispatch(clearHeader());
    },
    getNewSearchResults(searchTerm) {
        dispatch(getNewSearchResults(searchTerm));
    },
    makeToast(text, type) {
        responseHandler.customToast(text, type, dispatch);
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(FieldRepProfileV2));

let colDefs = [
  { key: "inspectionType", name: "Survey Type", width: 300, dataSource: "inspectionType", filterType: FILTER_TYPE_NONE },
  { key: "customerName", name: "Mueller Customer", width: 300, dataSource: "customerName", filterType: FILTER_TYPE_NONE },
  { key: "fullName", name: "Insured's Name", width: 200, dataSource: "fullName", filterType: FILTER_TYPE_NONE },
  { key: "customerDueDate", name: "Due", width: 200, dataSource: "customerDueDate", filterType: FILTER_TYPE_NONE },
  { key: "dateAssigned", name: "Assigned", width: 200, dataSource: "dateAssigned", filterType: FILTER_TYPE_NONE },
  { key: "fieldAppointmentDate", name: "Appointment", width: 200, dataSource: "fieldAppointmentDate", filterType: FILTER_TYPE_NONE },
  { key: "controlNumber", name: "Control #", width: 100, dataSource: "controlNumber", filterType: FILTER_TYPE_NONE },
  { key: "mac", name: "MAC", width: 80, dataSource: "mac", filterType: FILTER_TYPE_NONE, formatter: FORMATTER_RIGHT_ALIGN },
  { key: "percentageEnhanced", name: "% Enhanced", width: 100, dataSource: "percentageEnhanced", filterType: FILTER_TYPE_NONE },
];
